import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const NGOInvite: React.FC = () => {
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invitations, setInvitations] = useState<any[]>([]);
  const [ngos, setNgos] = useState<any[]>([]);
  const [selectedNgo, setSelectedNgo] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [changesRequested, setChangesRequested] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    ngo_name: '',
    ngo_email: ''
  });

  useEffect(() => {
    loadInvitations();
    loadNGOs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadInvitations = async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from('ngo_invitations')
      .select('*')
      .eq('company_id', companyId)
      .order('invited_at', { ascending: false });

    if (!error && data) {
      setInvitations(data);
    }
  };

  const loadNGOs = async () => {
    if (!companyId) return;

    try {
      // Fetch NGOs first
      const { data: ngosData, error } = await supabase
        .from('ngos')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading NGOs:', error);
        return;
      }

      if (!ngosData || ngosData.length === 0) {
        setNgos([]);
        return;
      }

      setNgos(ngosData);
    } catch (err) {
      console.error('Error in loadNGOs:', err);
    }
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!userProfile?.id || !companyId) throw new Error('Not authenticated');

      // Call the edge function to send the email automatically
      // We use direct fetch here instead of supabase.functions.invoke to get the exact error message from the server
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/invite-ngo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          ngo_email: formData.ngo_email,
          ngo_name: formData.ngo_name,
          company_id: companyId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Edge Function failed with status ' + response.status);
      }

      setSuccess(`✅ Invitation sent automatically to ${formData.ngo_email}! They will receive an email shortly.`);
      setFormData({ ngo_name: '', ngo_email: '' });
      loadInvitations();
    } catch (err: any) {
      console.error('Invitation error:', err);
      setError(err.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClass = `status-${status}`;
    return <span className={`status-badge ${statusClass}`}>{status}</span>;
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to delete this invitation? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { error: deleteError } = await supabase
        .from('ngo_invitations')
        .delete()
        .eq('id', invitationId);

      if (deleteError) throw deleteError;

      setSuccess('Invitation deleted successfully');
      loadInvitations();
    } catch (err: any) {
      console.error('Delete error:', err);
      setError(err.message || 'Failed to delete invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyNGO = async (ngoId: string, action: 'verified' | 'rejected' | 'changes_requested') => {
    if (!companyId || !ngoId) return;

    if (action === 'rejected' && !rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    if (action === 'changes_requested' && !changesRequested.trim()) {
      setError('Please specify what changes are needed');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!userProfile?.id) throw new Error('Not authenticated');

      const updateData: any = {
        status: action === 'changes_requested' ? 'invited' : action, // Set to 'invited' so NGO can see the form again
        updated_at: new Date().toISOString()
      };

      if (action === 'rejected') {
        updateData.rejection_reason = rejectionReason;
      }

      if (action === 'changes_requested') {
        // Store changes requested message in rejection_reason field (or create a new field)
        updateData.rejection_reason = `Changes requested: ${changesRequested}`;
        updateData.status = 'invited'; // Reset to 'invited' so form shows again
      }

      if (action === 'verified') {
        updateData.verified_by = userProfile.id;
        updateData.verified_at = new Date().toISOString();
        updateData.profile_complete = true;
        updateData.rejection_reason = null; // Clear any previous rejection/changes messages
      }

      const { error: updateError } = await supabase
        .from('ngos')
        .update(updateData)
        .eq('id', ngoId);

      if (updateError) throw updateError;

      if (action === 'verified') {
        // Mark documents as verified
        await supabase
          .from('ngo_documents')
          .update({
            is_verified: true,
            verified_at: new Date().toISOString(),
            verified_by: userProfile.id
          })
          .eq('ngo_id', ngoId);
      }

      setSuccess(`NGO ${action === 'verified' ? 'verified' : action === 'rejected' ? 'rejected' : 'changes requested'} successfully`);
      setSelectedNgo(null);
      setRejectionReason('');
      setChangesRequested('');
      loadNGOs();
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.message || 'Failed to update NGO status');
    } finally {
      setLoading(false);
    }
  };

  const loadNgoDetails = async (ngoId: string) => {
    const { data, error } = await supabase
      .from('ngos')
      .select('*')
      .eq('id', ngoId)
      .single();

    if (!error && data) {
      setSelectedNgo(data);
    }
  };

  return (
    <div style={{ marginTop: '-1rem' }}>
      {/* NGO Overview section removed for minimalistic layout */}

      <div className="form-section">
        <h1>Invite NGO</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
          Invite NGOs to collaborate on your CSR projects
        </p>

        {error && (
          <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {success && (
          <div className="toast toast-success" style={{ position: 'relative', marginBottom: '1rem' }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>NGO Name *</label>
            <input
              type="text"
              name="ngo_name"
              value={formData.ngo_name}
              onChange={(e) => setFormData({ ...formData, ngo_name: e.target.value })}
              required
              placeholder="Enter NGO legal name"
            />
          </div>

          <div className="form-group">
            <label>NGO Email *</label>
            <input
              type="email"
              name="ngo_email"
              value={formData.ngo_email}
              onChange={(e) => setFormData({ ...formData, ngo_email: e.target.value })}
              required
              placeholder="ngo@example.com"
            />
            <div className="help-text">
              An invitation email will be sent automatically to this address
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>

      <div className="form-section">
        <h2>Invited NGOs</h2>

        {invitations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📧</div>
            <div className="empty-state-title">No invitations sent yet</div>
            <div className="empty-state-description">
              Start by inviting an NGO above
            </div>
          </div>
        ) : (
          <div className="table-container" style={{ overflowX: 'auto', width: '100%' }}>
            <table className="table" style={{ minWidth: '800px' }}>
              <thead>
                <tr>
                  <th>NGO Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Invited At</th>
                  <th>Invitation Link</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => {
                  const inviteLink = inv.invitation_token
                    ? `${window.location.origin}/ngo/signup?token=${inv.invitation_token}`
                    : 'Link not available';

                  return (
                    <tr key={inv.id}>
                      <td>{inv.ngo_name}</td>
                      <td>{inv.ngo_email}</td>
                      <td>{getStatusBadge(inv.status)}</td>
                      <td>{new Date(inv.invited_at).toLocaleDateString()}</td>
                      <td>
                        {inv.invitation_token ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="text"
                              readOnly
                              value={inviteLink}
                              style={{
                                flex: 1,
                                padding: '0.5rem',
                                fontSize: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius-sm)',
                                background: 'var(--bg)',
                                fontFamily: 'monospace',
                                maxWidth: '300px',
                                overflowX: 'auto',
                                whiteSpace: 'nowrap'
                              }}
                              onClick={(e) => (e.target as HTMLInputElement).select()}
                            />
                            <button
                              onClick={async () => {
                                try {
                                  // Try modern clipboard API first
                                  if (navigator.clipboard && navigator.clipboard.writeText) {
                                    await navigator.clipboard.writeText(inviteLink);
                                    setCopiedId(inv.id);
                                    setTimeout(() => setCopiedId(null), 3000);
                                  } else {
                                    // Fallback for older browsers or non-HTTPS
                                    const textArea = document.createElement('textarea');
                                    textArea.value = inviteLink;
                                    textArea.style.position = 'fixed';
                                    textArea.style.left = '-999999px';
                                    textArea.style.top = '-999999px';
                                    document.body.appendChild(textArea);
                                    textArea.focus();
                                    textArea.select();
                                    try {
                                      const successful = document.execCommand('copy');
                                      if (successful) {
                                        setCopiedId(inv.id);
                                        setTimeout(() => setCopiedId(null), 3000);
                                      } else {
                                        // Last resort: select the text so user can copy manually
                                        const input = document.querySelector(`input[value="${inviteLink}"]`) as HTMLInputElement;
                                        if (input) {
                                          input.select();
                                          setSuccess(`Link selected - press Ctrl+C to copy`);
                                          setTimeout(() => setSuccess(''), 3000);
                                        } else {
                                          setError('Unable to copy. Please select and copy the link manually.');
                                        }
                                      }
                                    } catch (err) {
                                      console.error('Copy failed:', err);
                                      setError('Unable to copy. Please select and copy the link manually.');
                                    } finally {
                                      document.body.removeChild(textArea);
                                    }
                                  }
                                } catch (err) {
                                  console.error('Clipboard error:', err);
                                  setError('Unable to copy. Please select and copy the link manually.');
                                }
                              }}
                              className="btn btn-sm"
                              style={{
                                padding: '0.5rem 0.75rem',
                                fontSize: '0.75rem',
                                background: copiedId === inv.id ? '#10b981' : 'var(--primary)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                              title="Copy invitation link"
                            >
                              {copiedId === inv.id ? '✓ Copied!' : '📋 Copy'}
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            No link available
                          </span>
                        )}
                      </td>
                      <td>
                        {inv.status?.toLowerCase() !== 'accepted' && (
                          <button
                            onClick={() => handleDeleteInvitation(inv.id)}
                            className="btn btn-danger"
                            style={{
                              padding: '0.5rem 1rem',
                              fontSize: '0.875rem',
                              background: 'var(--danger)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            disabled={loading}
                            title="Cancel invitation"
                          >
                            Cancel Invite
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* NGO Management Section */}
      <div className="form-section">
        <h2>NGO Profiles</h2>
        <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
          Review and verify NGO profiles that have been submitted
        </p>

        {ngos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏢</div>
            <div className="empty-state-title">No NGOs registered yet</div>
            <div className="empty-state-description">
              NGOs will appear here after they complete their profile
            </div>
          </div>
        ) : (
          <div style={{
            width: '100%',
            overflowX: 'auto',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-md)'
          }}>
            <div style={{
              minWidth: '800px',
              maxHeight: '500px',
              overflowY: 'auto',
              overflowX: 'auto'
            }}>
              <table className="table" style={{ marginBottom: 0, width: '100%', minWidth: '800px' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10 }}>
                  <tr>
                    <th style={{ minWidth: '150px', padding: '1rem' }}>Legal Name</th>
                    <th style={{ minWidth: '120px', padding: '1rem' }}>Registration Type</th>
                    <th style={{ minWidth: '220px', padding: '1rem' }}>Contact Email</th>
                    <th style={{ minWidth: '120px', padding: '1rem' }}>Status</th>
                    <th style={{ minWidth: '120px', padding: '1rem' }}>Submitted At</th>
                    <th style={{ minWidth: '200px', padding: '1rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ngos.map((ngo) => (
                    <tr key={ngo.id}>
                      <td style={{ padding: '1rem' }}>{ngo.legal_name || 'N/A'}</td>
                      <td style={{ padding: '1rem' }}>{ngo.registration_type || 'N/A'}</td>
                      <td style={{ padding: '1rem' }}>{ngo.contact?.email || 'N/A'}</td>
                      <td style={{ padding: '1rem' }}>{getStatusBadge(ngo.status)}</td>
                      <td style={{ padding: '1rem' }}>{ngo.created_at ? new Date(ngo.created_at).toLocaleDateString() : 'N/A'}</td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => loadNgoDetails(ngo.id)}
                            className="btn btn-sm btn-outline"
                          >
                            View Details
                          </button>
                          {ngo.status === 'submitted' && (
                            <button
                              onClick={() => handleVerifyNGO(ngo.id, 'verified')}
                              className="btn btn-sm btn-primary"
                              disabled={loading}
                            >
                              ✓ Verify
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* NGO Details Modal */}
      {selectedNgo && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }} onClick={() => setSelectedNgo(null)}>
          <div className="modal-content" style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            position: 'relative'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2>NGO Profile Details</h2>
                <button
                  onClick={() => setSelectedNgo(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem'
                  }}
                >
                  ×
                </button>
              </div>

              <div className="card" style={{ marginBottom: '1rem' }}>
                <h3>Basic Information</h3>
                <div className="form-row">
                  <div>
                    <strong>Legal Name:</strong>
                    <p>{selectedNgo.legal_name || 'N/A'}</p>
                  </div>
                  <div>
                    <strong>Registration Type:</strong>
                    <p>{selectedNgo.registration_type || 'N/A'}</p>
                  </div>
                  <div>
                    <strong>PAN:</strong>
                    <p>{selectedNgo.pan || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: '1rem' }}>
                <h3>Address</h3>
                <p>
                  {selectedNgo.address?.street || ''}<br />
                  {selectedNgo.address?.city || ''}, {selectedNgo.address?.state || ''} {selectedNgo.address?.pincode || ''}<br />
                  {selectedNgo.address?.country || ''}
                </p>
              </div>

              <div className="card" style={{ marginBottom: '1rem' }}>
                <h3>Contact Information</h3>
                <div className="form-row">
                  <div>
                    <strong>Name:</strong>
                    <p>{selectedNgo.contact?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <strong>Email:</strong>
                    <p>{selectedNgo.contact?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <strong>Phone:</strong>
                    <p>{selectedNgo.contact?.phone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: '1rem' }}>
                <h3>Bank Details</h3>
                <div className="form-row">
                  <div>
                    <strong>Account Number:</strong>
                    <p>{selectedNgo.bank_details?.account_number || 'N/A'}</p>
                  </div>
                  <div>
                    <strong>IFSC Code:</strong>
                    <p>{selectedNgo.bank_details?.ifsc || 'N/A'}</p>
                  </div>
                </div>
                <div className="form-row">
                  <div>
                    <strong>Bank Name:</strong>
                    <p>{selectedNgo.bank_details?.bank_name || 'N/A'}</p>
                  </div>
                  <div>
                    <strong>Branch:</strong>
                    <p>{selectedNgo.bank_details?.branch || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {selectedNgo.status === 'submitted' && (
                <div className="card">
                  <h3>Verification Actions</h3>

                  <div className="form-group">
                    <label>Rejection Reason (if rejecting)</label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Provide reason for rejection..."
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Changes Requested (if requesting changes)</label>
                    <textarea
                      value={changesRequested}
                      onChange={(e) => setChangesRequested(e.target.value)}
                      placeholder="Specify what changes are needed..."
                      rows={3}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => handleVerifyNGO(selectedNgo.id, 'rejected')}
                      disabled={loading}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={() => handleVerifyNGO(selectedNgo.id, 'changes_requested')}
                      disabled={loading || !changesRequested.trim()}
                    >
                      Request Changes
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => handleVerifyNGO(selectedNgo.id, 'verified')}
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : '✓ Approve & Verify'}
                    </button>
                  </div>
                </div>
              )}


              {selectedNgo.rejection_reason && (
                <div className="card" style={{ background: '#fee2e2' }}>
                  <h3>Rejection Reason</h3>
                  <p>{selectedNgo.rejection_reason}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NGOInvite;

